// src/pages/tour-packages/useTourPackagesPage.ts

import { useEffect, useMemo, useState } from "react";

import { useToursList } from "@/api/features/tours/queries";
import { useDeleteTour } from "@/api/features/tours/mutations";

import type {
  TourStatus,
  TourListItemHeader,
} from "@/api/features/tours/types";

/* ============================================================================
   Types – UI-level shapes (for table + filters)
============================================================================ */

export type TourRow = {
  id: number;

  name: string;
  tourCode?: string;

  tourTypeName?: string;
  supplierName?: string;

  nights: number;
  days: number;

  primaryCountry?: string;
  primaryCity?: string;

  currencyCode?: string;

  status: TourStatus;
  isMultiCity?: boolean;

  updatedAt?: string;
};

export type TourFilters = {
  name?: string;
  tourCode?: string;
  tourType?: string;
  supplier?: string;
  status?: TourStatus | "ALL";
  country?: string;
  city?: string;
  currencyCode?: string;
};

/* ============================================================================
   Mapping helper – API ↔ UI
============================================================================ */

function mapTourToRow(t: TourListItemHeader): TourRow {
  return {
    id: t.id,
    name: t.name,
    tourCode: undefined,

    tourTypeName: t.tourType?.name ?? undefined,
    supplierName: t.supplier?.name ?? undefined,

    nights: t.nights,
    days: t.days,

    primaryCountry: undefined,
    primaryCity: undefined,

    currencyCode: t.currencyCode ?? undefined,

    status: t.status,
    isMultiCity: t.isMultiCity,

    updatedAt: t.updatedAt,
  };
}

/* ============================================================================
   Hook: state + logic for the listing page (ViewTourPackages)
============================================================================ */

export function useTourPackagesPage(pageSize: number = 10) {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<TourFilters>({});
  const [page, setPage] = useState(1);

  // Reset to first page on query/filter change
  useEffect(() => {
    setPage(1);
  }, [q, filters]);

  const statusParam =
    filters.status && filters.status !== "ALL" ? filters.status : undefined;

  const queryQ = useMemo(() => {
    const parts = [
      q,
      filters.name,
      filters.tourCode,
      filters.tourType,
      filters.supplier,
      filters.country,
      filters.city,
      filters.currencyCode,
    ]
      .map((x) => String(x ?? "").trim())
      .filter(Boolean);
    return parts.length ? parts.join(" ") : undefined;
  }, [
    q,
    filters.name,
    filters.tourCode,
    filters.tourType,
    filters.supplier,
    filters.country,
    filters.city,
    filters.currencyCode,
  ]);

  // React Query: list tours from API (already paginated)
  const { data, isLoading, isError, refetch, isFetching } = useToursList({
    page,
    pageSize,
    q: queryQ,
    status: statusParam,
    includeDeleted: false,
  });

  const apiItems: TourListItemHeader[] = data?.items ?? [];
  const total = data?.total ?? 0;

  // Map to UI rows
  const rows: TourRow[] = useMemo(
    () => apiItems.map(mapTourToRow),
    [apiItems]
  );

  const filtered: TourRow[] = rows;

  // Because the API is paginated, totalPages should be derived from backend total
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  // Serial start for this page (0-based)
  const start = (page - 1) * pageSize;
  const serialStart = start;

  // Rows to display for the current page (post-filter)
  const pageRows = rows;

  /* -------------------------- Delete (API) ---------------------------- */

  const deleteMutation = useDeleteTour();

  const deleteTour = (id: number | string) => {
    deleteMutation.mutate(Number(id), {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const clearFilters = () => setFilters({});

  return {
    // Data
    rows,
    filtered,
    pageRows,
    serialStart,
    total,
    isLoading,
    isFetching,
    isError,
    refetch,

    // Search
    q,
    setQ,

    // Filters
    filters,
    setFilters,
    clearFilters,

    // Pagination
    page,
    setPage,
    pageSize,
    totalPages,
    start,

    // Actions
    deleteTour,
  };
}
